import * as THREE from 'three';
import { EL_FIRE, EL_FROST, EL_LIGHTNING, EL_TEMPORAL } from '../sim/world';
import { bulletGeometry, mergeGeometries } from './geometry';

export interface ProjectileLook {
  id: string;
  color: number;
  geometry: THREE.BufferGeometry;
  width: number;
  length: number;
  trailLength: number;
  trailOpacity: number;
  spin: number;
  launchCount: number;
  impactCount: number;
}

function transformed(geometry: THREE.BufferGeometry, scale: [number, number, number], position: [number, number, number] = [0, 0, 0], rotation: [number, number, number] = [0, 0, 0]): THREE.BufferGeometry {
  const out = geometry.clone();
  out.scale(...scale);
  out.rotateX(rotation[0]);
  out.rotateY(rotation[1]);
  out.rotateZ(rotation[2]);
  out.translate(...position);
  return out;
}

function shaft(tipScale = 1): THREE.BufferGeometry {
  return mergeGeometries([
    transformed(new THREE.CylinderGeometry(0.09, 0.09, 1.05, 6), [1, 1, 1], [0, 0, -0.08], [Math.PI / 2, 0, 0]),
    transformed(new THREE.ConeGeometry(0.22 * tipScale, 0.55, 5), [1, 1, 1], [0, 0, 0.7], [Math.PI / 2, 0, 0]),
  ]);
}

function spore(): THREE.BufferGeometry {
  return mergeGeometries([
    transformed(new THREE.IcosahedronGeometry(0.43, 1), [1, 1, 1]),
    transformed(new THREE.SphereGeometry(0.18, 6, 4), [1, 1, 1], [0.35, 0.12, -0.15]),
    transformed(new THREE.SphereGeometry(0.14, 6, 4), [1, 1, 1], [-0.31, -0.08, 0.18]),
  ]);
}

function arrow(): THREE.BufferGeometry {
  return mergeGeometries([
    shaft(0.8),
    transformed(new THREE.ConeGeometry(0.22, 0.34, 3), [1, 0.35, 1], [0, 0, -0.62], [-Math.PI / 2, 0, 0]),
  ]);
}

function pulse(): THREE.BufferGeometry {
  return mergeGeometries([
    transformed(new THREE.BoxGeometry(0.42, 0.22, 0.86), [1, 1, 1]),
    transformed(new THREE.OctahedronGeometry(0.28, 0), [1, 0.6, 1.3], [0, 0, 0.48]),
  ]);
}

const BASE_LOOKS: ProjectileLook[] = [
  { id: 'spore', color: 0x7df9ff, geometry: spore(), width: 1, length: 1, trailLength: 1.4, trailOpacity: 0.22, spin: 5, launchCount: 5, impactCount: 5 },
  { id: 'bone-spear', color: 0xfff1a8, geometry: shaft(1.2), width: 0.72, length: 1.65, trailLength: 2.1, trailOpacity: 0.2, spin: 0, launchCount: 4, impactCount: 8 },
  { id: 'bronze-arrow', color: 0xffd27d, geometry: arrow(), width: 0.58, length: 1.55, trailLength: 1.7, trailOpacity: 0.18, spin: 0, launchCount: 7, impactCount: 5 },
  { id: 'steel-bolt', color: 0x7df9ff, geometry: shaft(1.5), width: 0.82, length: 1.85, trailLength: 2.4, trailOpacity: 0.26, spin: 0, launchCount: 5, impactCount: 10 },
  { id: 'flintlock-ball', color: 0xffffff, geometry: transformed(new THREE.DodecahedronGeometry(0.5, 0), [0.8, 0.8, 1.25]), width: 1.1, length: 1.2, trailLength: 2.8, trailOpacity: 0.34, spin: 14, launchCount: 14, impactCount: 16 },
  { id: 'pulse', color: 0x8cff9e, geometry: pulse(), width: 0.75, length: 1.35, trailLength: 2, trailOpacity: 0.35, spin: 0, launchCount: 4, impactCount: 6 },
  { id: 'chrono-ring', color: 0xc9a7ff, geometry: mergeGeometries([bulletGeometry('ring').clone(), transformed(new THREE.SphereGeometry(0.18, 6, 4), [1, 1, 1])]), width: 1.25, length: 1.25, trailLength: 1.8, trailOpacity: 0.3, spin: 7, launchCount: 9, impactCount: 12 },
];

const ELEMENT_LOOKS: ProjectileLook[] = [
  { id: 'fire', color: 0xff7a2a, geometry: bulletGeometry('blob'), width: 1.25, length: 1.6, trailLength: 2.4, trailOpacity: 0.38, spin: 3, launchCount: 8, impactCount: 12 },
  { id: 'frost', color: 0x7ce0ff, geometry: bulletGeometry('shard'), width: 1.1, length: 1.35, trailLength: 2, trailOpacity: 0.3, spin: 7, launchCount: 7, impactCount: 11 },
  { id: 'lightning', color: 0xfff35a, geometry: bulletGeometry('star'), width: 1.2, length: 1.05, trailLength: 1.5, trailOpacity: 0.42, spin: 22, launchCount: 10, impactCount: 14 },
  { id: 'temporal', color: 0xc07dff, geometry: bulletGeometry('ring'), width: 1.35, length: 1.2, trailLength: 2.2, trailOpacity: 0.34, spin: 8, launchCount: 9, impactCount: 13 },
];

const GENERIC_LOOKS: ProjectileLook[] = (['orb', 'shard', 'ring', 'needle', 'blob', 'star'] as const).map((visual, index) => ({
  id: `generic-${visual}`,
  color: 0xffffff,
  geometry: bulletGeometry(visual),
  width: 1,
  length: visual === 'needle' ? 1.5 : visual === 'shard' ? 1.25 : 1,
  trailLength: visual === 'needle' ? 1.8 : 1.25,
  trailOpacity: 0.2,
  spin: visual === 'star' ? 16 : visual === 'ring' || visual === 'shard' ? 6 : 0,
  launchCount: 5 + (index % 3),
  impactCount: 6 + (index % 4),
}));

export const PLAYER_PROJECTILE_LOOKS = [...BASE_LOOKS, ...ELEMENT_LOOKS, ...GENERIC_LOOKS] as const;

export function projectileLookIndex(color: number, visual: number, elements: number, seed = 0): number {
  if (elements) {
    const active: number[] = [];
    if (elements & EL_FIRE) active.push(0);
    if (elements & EL_FROST) active.push(1);
    if (elements & EL_LIGHTNING) active.push(2);
    if (elements & EL_TEMPORAL) active.push(3);
    return BASE_LOOKS.length + active[Math.abs(seed) % active.length];
  }
  if (color === 0xfff1a8) return 1;
  if (color === 0xffd27d) return 2;
  if (color === 0xffffff) return 4;
  if (color === 0x8cff9e) return 5;
  if (color === 0xc9a7ff) return 6;
  // The starter spore and crossbow deliberately share the readability cyan;
  // their source bullet shapes distinguish the soft orb from the steel bolt.
  if (color === 0x7df9ff && visual === 3) return 3;
  return BASE_LOOKS.length + ELEMENT_LOOKS.length + Math.max(0, Math.min(GENERIC_LOOKS.length - 1, visual));
}

export function projectileLook(color: number, visual: number, elements: number, seed = 0): ProjectileLook {
  return PLAYER_PROJECTILE_LOOKS[projectileLookIndex(color, visual, elements, seed)];
}
